import PropTypes from "prop-types";
import * as React from "react";
import moment from "moment";
import settings from "settings";
import get from "lodash/get";
import isNull from "lodash/isNull";
import { translate, Interpolate } from "react-i18next";

import { x } from "helpers/localization";
import { isoToFormattedLocalDate, isoToFormattedLocalTime } from "helpers/datetime";
import { ClanInputWrapper } from "../UIKit";

import { ButtonDublon, Button, ButtonGroup, DialogContainer, DialogHeader, DialogBody, DialogFooter, Divider, Gold, ErrorDublon, Message, Toggle } from "@wg/wows-react-uikit";

import { TooltipBody } from "components";

import { ATTRIBUTES_VALIDATION_STATE } from "fe2/Components/UIKit/ClanCreate/Constants";

import styles from "./ClanEditDialogs.scss";

const propTypes = {
  clanName: PropTypes.string.isRequired,
  clanTag: PropTypes.string.isRequired,
  currentAccount: PropTypes.shape({
    totalGold: PropTypes.number.isRequired,
    isFreeRenameAvailable: PropTypes.bool,
    nextRenameAvailableAt: PropTypes.string
  }).isRequired,
  name: PropTypes.shape({
    value: PropTypes.string,
    isValidating: PropTypes.bool.isRequired,
    available: PropTypes.string.isRequired,
    error: PropTypes.string
  }).isRequired,
  tag: PropTypes.shape({
    value: PropTypes.string,
    isValidating: PropTypes.bool.isRequired,
    available: PropTypes.string.isRequired,
    error: PropTypes.string
  }).isRequired,

  error: PropTypes.object,
  isProcessing: PropTypes.bool,

  checkRenamingClanStatus: PropTypes.func.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  onRenameClanClick: PropTypes.func.isRequired,
  onUpdateFieldValue: PropTypes.func.isRequired,
  onValidateOnServer: PropTypes.func.isRequired,

  onClickClansRules: PropTypes.func,

  t: PropTypes.func.isRequired
};

class ClanNameEditDialog extends React.Component {


  componentDidMount() {
    if (this.props.isProcessing) {
      this.props.checkRenamingClanStatus();
    }
  }

  renderHeaderMessage(account) {
    const {
      t
    } = this.props;
    if (settings.clanRename.renameCooldown) {
      if (account.nextRenameAvailableAt && !account.isFreeRenameAvailable) {
        return <Message type="error">
                        {t('Тег и название были недавно изменены. Повторное изменение станет доступно %(date)s в %(time)s', {
            date: isoToFormattedLocalDate(account.nextRenameAvailableAt),
            time: isoToFormattedLocalTime(account.nextRenameAvailableAt)
          })}
                    </Message>;
      }
      return <Message>{t('Изменение тега и названия возможно один раз в %(count)s дней.', { count: moment.duration(settings.clanRename.renameCooldown, 'seconds').days() })}</Message>;
    }
    return null;
  }

  renderFooterMessage(account, clanRulesToggle) {
    if (account.isFreeRenameAvailable) {
      const message = account.roleName === 'commander' ? x('Как командующий флотом, вы можете 1 раз бесплатно изменить тег и название клана. В следующий раз стоимость переименования составит %(dublons)s') : x('Командующий флотом может 1 раз бесплатно изменить тег и название клана. Как заместитель командующего, вы можете изменить тег и название за %(dublons)s');

      return <div>
                    <div className={styles.rules}>
                        <Interpolate i18nKey={x('Администрация игры изменила тег и название вашего клана, так как они нарушали %(rules)s')} rules={clanRulesToggle} />
                    </div>
                    <div className={styles.info}>
                        <Interpolate i18nKey={message} dublons={<Gold amount={settings.clanRename.cost} />} />
                    </div>
                </div>;
    }
    return null;
  }

  renderDialogHeader(currentAccount) {
    const headerMessage = this.renderHeaderMessage(currentAccount);
    return headerMessage ? <div>
                <div className={styles.headerMessage}>
                    {headerMessage}
                </div>
                <Divider type="major" />
            </div> : null;
  }

  renderDialogFooter(currentAccount, needGold, isButtonDisabled, tooltipContent) {
    const hasFreeRenameAccess = currentAccount.isFreeRenameAvailable && currentAccount.roleName === 'commander';
    const cost = hasFreeRenameAccess ? 0 : settings.clanRename.cost;
    const t = this.props.t;
    const clanRulesToggle = <Toggle caption={t('Правила кланов')} onClick={this.props.onClickClansRules} />;
    const footerMessage = this.renderFooterMessage(currentAccount, clanRulesToggle);
    const clanRules = footerMessage ? null : <div className={styles.rules}>
                    <Interpolate i18nKey={x('При изменении тега и названия соблюдайте %(clanRules)s')} clanRules={clanRulesToggle} />
                </div>;
    const renameMessage = <div className={styles.info}>{t('Переименование может занять некоторое время')}</div>;

    if (needGold === 0 || !cost) {
      return <div>
                    {clanRules}
                    {footerMessage}
                    {renameMessage}
                    <ButtonGroup isLoading={this.props.isProcessing} spinnerLabel={t('Переименовываем клан')}>
                        <ButtonDublon caption={t('Сохранить')} amount={cost} isDisabled={isButtonDisabled} isJustified isHighlighted onClick={this.props.onRenameClanClick} tooltipContent={tooltipContent ? <TooltipBody>{tooltipContent}</TooltipBody> : null} />
                        <Button isJustified onClick={this.props.onCloseClick}>{t('Отменить')}</Button>
                    </ButtonGroup>
                </div>;
    }

    return <div>
                {clanRules}
                {renameMessage}
                <ErrorDublon caption={t('Стоимость переименования')} amount={settings.clanRename.cost} deficit={needGold} />
            </div>;
  }

  checkButtonDisabled(account) {
    const {
      t
    } = this.props;
    let tooltipContent = null;

    // rename cooldown – button disabled permanently if free rename is not available
    if (account.nextRenameAvailableAt && !account.isFreeRenameAvailable) {
      tooltipContent = t('Тег и название были недавно изменены. Повторное изменение станет доступно %(date)s в %(time)s', {
        date: isoToFormattedLocalDate(account.nextRenameAvailableAt),
        time: isoToFormattedLocalTime(account.nextRenameAvailableAt)
      });
      return { isButtonDisabled: true, tooltipContent };
    }

    const anyFieldError = this.props.tag.error || this.props.name.error;
    const allFieldsAvailable = this.props.tag.available === ATTRIBUTES_VALIDATION_STATE.AVAILABLE && this.props.name.available === ATTRIBUTES_VALIDATION_STATE.AVAILABLE;
    const anyFieldChanged = (this.props.tag.value && this.props.tag.value !== this.props.clanTag) || (this.props.name.value && this.props.name.value !== this.props.clanName);

    if (anyFieldError) {
      tooltipContent = t('Введите корректные тег и название');
    } else if (this.props.tag.available === ATTRIBUTES_VALIDATION_STATE.NOT_AVAILABLE || this.props.name.available === ATTRIBUTES_VALIDATION_STATE.NOT_AVAILABLE) {
      tooltipContent = t('Клан с таким тегом или названием уже зарегистрирован');
    }

    if (!anyFieldChanged) {
      tooltipContent = t('Измените тег или название');
    }

    return { isButtonDisabled: !(allFieldsAvailable && !anyFieldError && anyFieldChanged), tooltipContent };
  }

  render() {
    const {
      t,
      currentAccount
    } = this.props;
    const error = this.props.error || {};
    const inputsErrors = {
      tag: get(error, 'errors.tag', [])[0] || null,
      name: get(error, 'errors.name', [])[0] || null
    };

    let {
      isButtonDisabled,
      tooltipContent
    } = this.checkButtonDisabled(currentAccount);


    if (inputsErrors.tag || inputsErrors.name) {
      isButtonDisabled = true;
      tooltipContent = t('Введите корректные тег и название');
    }

    let needGold = 0;
    if (error.message) {
      isButtonDisabled = true;
      if (error.message === 'NO_GOLD') {
        needGold = settings.clanRename.cost - currentAccount.totalGold;
      }
    }

    const tagValue = isNull(this.props.tag.value) ? this.props.clanTag : this.props.tag.value;
    const nameValue = isNull(this.props.name.value) ? this.props.clanName : this.props.name.value;
    const footer = this.renderDialogFooter(currentAccount, needGold, isButtonDisabled, tooltipContent);
    return <DialogContainer>
                <DialogHeader>{t('Тег и название')}</DialogHeader>
                <DialogBody>
                    {this.renderDialogHeader(currentAccount)}
                    <div className={styles.clanRename}>
                        <div className={styles.inputTag}>
                            <ClanInputWrapper isFocusOn field="tag" value={tagValue} isDisabled={this.props.isProcessing} isValidating={this.props.tag.isValidating} error={inputsErrors.tag || this.props.tag.error} onChange={this.props.onUpdateFieldValue} onValidateOnServer={this.props.onValidateOnServer} />
                        </div>
                        <ClanInputWrapper field="name" value={nameValue} isDisabled={this.props.isProcessing} isValidating={this.props.name.isValidating} error={inputsErrors.name || this.props.name.error} onChange={this.props.onUpdateFieldValue} onValidateOnServer={this.props.onValidateOnServer} />
                    </div>
                </DialogBody>
                <DialogFooter>{footer}</DialogFooter>
            </DialogContainer>;
  }
}

ClanNameEditDialog.propTypes = propTypes;

export default translate()(ClanNameEditDialog);